// Water animation — the live, pointer-reactive WebGL cell pattern used as a
// block background.
//
// Ported from the standalone prototype in `.temp/Animations/bosaq-water/index.html`,
// stripped of its control panel, video recorder and SVG exporter. The shader and
// the seed simulation are unchanged: cells are a power diagram over a drifting,
// curl-noise-advected seed field, and the pointer pushes seeds around with a
// spring-damped wake.
//
// How the water looks — speed, cell size, line thickness, colours — is set in
// DEFAULTS below and can be overridden per placement from the template, which
// passes the overrides down as data attributes. It stays out of the CMS on
// purpose: these get dialled in once per block by whoever builds it, and are not
// worth a screen of editor fields.
//
// Several blocks on one page can run their own animation. They share nothing but
// the plumbing:
//
//   * one requestAnimationFrame loop drives every playing instance, so the page
//     has a single tick rather than one per block;
//   * one set of window pointer listeners fans out to the live instances;
//   * WebGL contexts are pooled. Browsers cap how many can exist at once and
//     silently kill the oldest when you go over, so we cap it ourselves and
//     evict the instance that has been off screen longest;
//   * simulation state is per instance, so no block's water reacts to another's.
//
// Per-instance cost is kept down by only stepping instances that are actually on
// screen, and by bucketing seeds into a uniform grid before the neighbour pass —
// that pass is the one part of the simulation that would otherwise be O(n²).

// What a block's water looks like. Each of these can be overridden per
// placement through a data attribute — see `_components/water-animation.twig`,
// which is the only thing that should be setting them. They stay out of the CMS
// on purpose: an editor picking a ripple strength is a screen of fields for
// something that gets dialled in once.
const DEFAULTS = {
  // Cell size in screen px. Smaller value -> more, smaller cells.
  targetCellPx: 120,
  // Channel/line thickness in screen px. Stays constant at any viewport size.
  linePx: 10,
  // Corner rounding. 0.25 reproduces the original shader's smooth-min radius.
  roundness: 0.25,
  // Flow speed multiplier.
  speed: 1,
  // Pointer push strength.
  ripple: 1,
  // Pointer influence radius, in p-space (1 unit = the canvas height).
  reach: 0.26,
  // Flat colour set — gradients are phase 2.
  waterColor: "#015cfa", // ELECTRIC BLUE 500
  lineColor: "#ebf2ff", // MIST 200
};

// Page-wide plumbing. Not per block: these are about what the browser and the
// GPU can take, which is a property of the page, not of any one placement.
const CONFIG = {
  // Fragment budget per instance. The shader costs one pass per pixel per seed,
  // so this is the number that keeps it affordable. Sized for two instances
  // being on screen at once rather than one, and fixed rather than divided
  // between whatever happens to be visible — a budget that moved with the
  // scroll position would visibly re-resolve the canvas mid-scroll.
  pixelBudget: 2000000,
  minDpr: 0.7,
  maxDpr: 1.75,
  // Live WebGL contexts allowed at once, across every block on the page.
  maxContexts: 4,
  // How early a context is created / the loop resumes.
  rootMargin: "200px",
  // How long the block has to hold still before the seed field is rebuilt.
  // A ResizeObserver fires on every pixel of a drag, and rebuilding reseeds the
  // whole field — that reads as a hard flash, once per pixel. The canvas stretches
  // in the meantime, which is a far cheaper artefact than strobing.
  resizeDebounceMs: 100,
};

const HEX = /^#?[0-9a-f]{6}$/i;

// Turn an "rgb(...)"/"rgba(...)" string into [r,g,b] in 0..1. Returns null for a
// transparent colour (alpha 0) or anything it can't read, so callers can fall
// back to a solid default rather than painting nothing.
function parseRgb(value) {
  const v = (value || "").trim();
  const m =
    /^rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)(?:[\s,/]+([\d.]+)(%?))?\s*\)$/i.exec(
      v
    );
  if (m) {
    let alpha = m[4] === undefined ? 1 : parseFloat(m[4]);
    if (m[5] === "%") alpha /= 100;
    if (alpha === 0) return null;
    return [+m[1] / 255, +m[2] / 255, +m[3] / 255];
  }
  // Chrome computes `color-mix(in srgb, …)` (used by the water band's fill) to
  // `color(srgb r g b [/ a])` with 0..1 components, which the rgb() regex above
  // misses — parse it too, else the fill silently falls back to the default and
  // the band paints flat (fill == line == accent, no visible pattern).
  const c =
    /^color\(\s*srgb\s+([\d.]+)\s+([\d.]+)\s+([\d.]+)(?:\s*\/\s*([\d.]+))?\s*\)$/i.exec(
      v
    );
  if (c) {
    const alpha = c[4] === undefined ? 1 : parseFloat(c[4]);
    if (alpha === 0) return null;
    return [+c[1], +c[2], +c[3]];
  }
  return null;
}

// Resolve a CSS colour expression in the block's own cascade context. A hidden
// probe appended to the host inherits `--accent` (and every other custom
// property) from the block, and the browser computes hex / rgb() / oklch() /
// named colours down to an rgb() string for us — so the module never parses a
// colour space by hand and a token swap flows through automatically. Returns
// null when the expression resolves to nothing usable (rejected value, unset
// var, transparent), letting the caller drop back to its default.
function computeColor(host, expression) {
  const probe = document.createElement("span");
  probe.style.color = expression;
  // A value the CSS parser rejected outright leaves `color` unset.
  if (!probe.style.color) return null;
  probe.style.display = "none";
  host.appendChild(probe);
  const resolved = getComputedStyle(probe).color;
  host.removeChild(probe);
  return parseRgb(resolved);
}

// Overrides arrive as data attributes so the markup stays declarative and the
// module keeps its defaults in one place. Anything missing or unparseable falls
// back rather than throwing — a typo in a template should not take the
// animation down.
function readOptions(host) {
  const num = (key, fallback) => {
    const raw = host.dataset[key];
    if (raw === undefined || raw === "") return fallback;
    const n = Number(raw);
    return Number.isFinite(n) ? n : fallback;
  };

  // Colour follows the block's accent token by default, so the same animation
  // renders in whatever `accent-*` class the block carries — one source of
  // truth, zero per-block hexes. An explicit six-digit hex from the template
  // still wins. `accent-none`, an unset token, or a transparent value all fall
  // back to the brand default rather than painting nothing.
  const color = (key, expression, fallbackHex) => {
    const raw = host.dataset[key];
    if (raw && HEX.test(raw)) return hexToRgb(raw);
    // A non-hex override (e.g. "var(--accent)" or "color-mix(in srgb, var(--accent) 18%, white)")
    // is used AS the expression, so a block can invert or tint the accent — the
    // water band paints channels = accent and fill = light accent. Missing/empty
    // falls back to the default expression (accent-driven).
    return computeColor(host, raw || expression) ?? hexToRgb(fallbackHex);
  };

  const lineColor = color(
    "waterLineColor",
    `var(--water-line, ${DEFAULTS.lineColor})`,
    DEFAULTS.lineColor
  );
  // Optional second channel colour: when set, the lines run as a left→right
  // gradient from lineColor to this. Unset → equals lineColor (a flat line).
  const rawLine2 = host.dataset.waterLineColor2;
  const lineColor2 = rawLine2
    ? HEX.test(rawLine2)
      ? hexToRgb(rawLine2)
      : computeColor(host, rawLine2) ?? lineColor
    : lineColor;

  return {
    targetCellPx: num("waterCellSize", DEFAULTS.targetCellPx),
    linePx: num("waterLineThickness", DEFAULTS.linePx),
    roundness: num("waterRoundness", DEFAULTS.roundness),
    speed: num("waterSpeed", DEFAULTS.speed),
    ripple: num("waterRipple", DEFAULTS.ripple),
    reach: num("waterReach", DEFAULTS.reach),
    // Water reads from `--accent`; line reads from an optional `--water-line`,
    // defaulting to MIST 200 when the block sets none.
    waterColor: color("waterColor", "var(--accent, transparent)", DEFAULTS.waterColor),
    lineColor: lineColor,
    lineColor2: lineColor2,
    // Lines-only: transparent cells so the canvas overlays a photo/tint.
    linesOnly: num("waterLinesOnly", 0) ? 1 : 0,
  };
}

const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

// Every controller with a live WebGL context, and the subset currently animating.
const live = new Set();
const playing = new Set();
let tickerId = 0;
let pointerBound = false;

// ---------------------------------------------------------------- shared tick

function tick(now) {
  // Re-arm first: an instance removing itself mid-iteration must not strand the
  // loop, and a still-populated set must not lose its next frame.
  tickerId = playing.size ? requestAnimationFrame(tick) : 0;
  playing.forEach((controller) => controller.advance(now));
}

function startTicker() {
  if (!tickerId && playing.size) tickerId = requestAnimationFrame(tick);
}

// ------------------------------------------------------------- shared pointer

function fanOutPointer(clientX, clientY) {
  live.forEach((controller) => controller.trackPointer(clientX, clientY));
}

function clearPointer() {
  live.forEach((controller) => controller.clearPointer());
}

function bindPointer() {
  if (pointerBound) return;
  pointerBound = true;

  // Listened for on the window rather than on each canvas: a canvas sits behind
  // its block's copy and CTAs and is pointer-events:none so it never swallows
  // their clicks, but the water should still react while the cursor is over
  // them. Each instance bounds-checks, so this is a no-op away from the blocks.
  window.addEventListener(
    "mousemove",
    (e) => fanOutPointer(e.clientX, e.clientY),
    { passive: true }
  );
  window.addEventListener(
    "touchmove",
    (e) => {
      const touch = e.touches[0];
      if (touch) fanOutPointer(touch.clientX, touch.clientY);
    },
    { passive: true }
  );
  window.addEventListener("touchend", clearPointer);
  document.addEventListener("mouseleave", clearPointer);
}

// ------------------------------------------------------------- context pooling

// Free a context for a newcomer by dropping the instance that has been off
// screen the longest. Anything currently on screen is left alone.
function makeRoomForContext() {
  if (live.size < CONFIG.maxContexts) return true;

  let victim = null;
  live.forEach((controller) => {
    if (controller.isVisible) return;
    if (!victim || controller.lastVisibleAt < victim.lastVisibleAt) {
      victim = controller;
    }
  });

  if (!victim) return false;
  victim.releaseContext();
  return true;
}

// ------------------------------------------------------------------- renderer

function hexToRgb(hex) {
  const m = /^#?([0-9a-f]{6})$/i.exec((hex || "").trim());
  if (!m) return [0, 0, 0];
  const n = parseInt(m[1], 16);
  return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
}

const VERTEX_SHADER = `
attribute vec2 aPos;
varying vec2 vUv;
void main(){ vUv = vec2(aPos.x*0.5+0.5, 0.5-aPos.y*0.5); gl_Position = vec4(aPos,0.0,1.0); }
`;

// Each seed packs into one vec3 uniform (xy = position, z = weight), so the seed
// count is bounded by the GPU's fragment uniform budget. Built as a function so
// we can shrink it to whatever the driver will actually link.
function fragmentShader(maxSeeds, hasDeriv, precision) {
  return `
${hasDeriv ? "#extension GL_OES_standard_derivatives : enable" : ""}
precision ${precision} float;
#define MAX ${maxSeeds}
uniform vec3 uSeedW[MAX];
uniform int uCount;
uniform vec3 uCell;
uniform vec3 uBorder;
uniform vec3 uBorder2;
uniform float uLinesOnly;
uniform float uAspect;
uniform float uBorderW;
uniform float uRound;
varying vec2 vUv;
void main(){
  vec2 p = vec2(vUv.x*uAspect, vUv.y);
  vec2 mr = vec2(0.0); float md = 1e9; int mi = 0; float wA = 0.0;
  for(int i=0;i<MAX;i++){
    if(i>=uCount) break;
    vec3 sw = uSeedW[i];
    vec2 r = sw.xy - p;
    float d = dot(r, r) - sw.z;
    if(d < md){ md = d; mr = r; mi = i; wA = sw.z; }
  }
  float bd1 = 1e9, bd2 = 1e9, sm = 1e9;
  float k = uRound;
  for(int i=0;i<MAX;i++){
    if(i>=uCount) break;
    if(i==mi) continue;
    vec3 sw = uSeedW[i];
    vec2 r = sw.xy - p;
    vec2 diff = r - mr;
    float len = max(length(diff), 1e-5);
    float d = (dot(mr+r, diff) + (wA - sw.z)) * 0.5 / len;
    if(d < bd1){ bd2 = bd1; bd1 = d; }
    else if(d < bd2){ bd2 = d; }
    float h = clamp(0.5 + 0.5*(d - sm)/k, 0.0, 1.0);
    sm = mix(d, sm, h) - k*h*(1.0-h);
  }
  float taper = smoothstep(0.0, uBorderW*4.0, bd2 - bd1);
  float W = mix(uBorderW*1.15, uBorderW*0.85, taper);
  float sd = sm - W;
  ${hasDeriv ? "float aa = fwidth(sd);" : "float aa = 0.0015;"}
  // Lines-only mode composites over the page, so a soft channel edge leaves a
  // semi-transparent halo that reads as a border on a light accent (lime). Tighten
  // the anti-alias band there for crisp, solid channels; normal mode keeps its full
  // soft edge for smooth on-canvas blending.
  float lineAA = aa * mix(1.0, 0.4, uLinesOnly);
  float line = smoothstep(-lineAA, lineAA, sd);
  // Channel colour can run as a left→right gradient (uBorder → uBorder2). When a
  // caller sets no second colour it equals the first, so this stays a flat line.
  vec3 lineCol = mix(uBorder, uBorder2, vUv.x);
  // Lines-only mode (uLinesOnly = 1): the cells go transparent (alpha follows
  // 1 - line) so whatever sits behind the canvas shows through, and only the
  // channel lines paint. Normal mode stays fully opaque.
  vec3 col = mix(mix(lineCol, uCell, line), lineCol, uLinesOnly);
  float alpha = mix(1.0, 1.0 - line, uLinesOnly);
  gl_FragColor = vec4(col, alpha);
}
`;
}

function createRenderer(canvas) {
  const opts = { alpha: true, premultipliedAlpha: false, antialias: true };
  const gl =
    canvas.getContext("webgl", opts) ||
    canvas.getContext("experimental-webgl", opts);
  if (!gl) return null;

  // Straight-alpha blending so the lines-only mode can composite over whatever
  // sits behind the canvas (a photo, a tint).
  gl.enable(gl.BLEND);
  gl.blendFunc(gl.SRC_ALPHA, gl.ONE_MINUS_SRC_ALPHA);

  const hasDeriv = !!gl.getExtension("OES_standard_derivatives");
  const precision =
    gl.getShaderPrecisionFormat(gl.FRAGMENT_SHADER, gl.HIGH_FLOAT).precision > 0
      ? "highp"
      : "mediump";

  const compile = (type, src) => {
    const shader = gl.createShader(type);
    gl.shaderSource(shader, src);
    gl.compileShader(shader);
    return shader;
  };

  // Try progressively smaller seed counts until one links.
  const limit = gl.getParameter(gl.MAX_FRAGMENT_UNIFORM_VECTORS) || 16;
  const headroom = Math.max(8, limit - 16);
  const candidates = [420, 320, 256, 192, 128, 96, 64, 48, 32, 16, 8].filter(
    (n) => n <= headroom
  );
  if (!candidates.length) candidates.push(8);

  let program = null;
  let maxSeeds = 0;
  for (const n of candidates) {
    const p = gl.createProgram();
    gl.attachShader(p, compile(gl.VERTEX_SHADER, VERTEX_SHADER));
    gl.attachShader(
      p,
      compile(gl.FRAGMENT_SHADER, fragmentShader(n, hasDeriv, precision))
    );
    gl.linkProgram(p);
    if (gl.getProgramParameter(p, gl.LINK_STATUS)) {
      program = p;
      maxSeeds = n;
      break;
    }
    gl.deleteProgram(p);
  }
  if (!program) return null;

  gl.useProgram(program);
  const quad = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, quad);
  gl.bufferData(
    gl.ARRAY_BUFFER,
    new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]),
    gl.STATIC_DRAW
  );
  const aPos = gl.getAttribLocation(program, "aPos");
  gl.enableVertexAttribArray(aPos);
  gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 0, 0);

  const uniforms = {
    seedW: gl.getUniformLocation(program, "uSeedW"),
    count: gl.getUniformLocation(program, "uCount"),
    cell: gl.getUniformLocation(program, "uCell"),
    border: gl.getUniformLocation(program, "uBorder"),
    border2: gl.getUniformLocation(program, "uBorder2"),
    aspect: gl.getUniformLocation(program, "uAspect"),
    borderW: gl.getUniformLocation(program, "uBorderW"),
    round: gl.getUniformLocation(program, "uRound"),
    linesOnly: gl.getUniformLocation(program, "uLinesOnly"),
  };

  return { gl, program, uniforms, maxSeeds, quad };
}

// ------------------------------------------------------------------ simulation

// One block's water: its own GL context, its own seed field, its own pointer.
// Knows nothing about scrolling or about the other instances on the page.
function createSimulation(host, canvas, opts) {
  const renderer = createRenderer(canvas);
  if (!renderer) return null;

  const { gl, program, uniforms, maxSeeds, quad } = renderer;
  // readOptions already resolved these to [r,g,b] in 0..1 (from the accent
  // token, an explicit hex, or the default).
  const { waterColor, lineColor, lineColor2 } = opts;

  const seedData = new Float32Array(maxSeeds * 3);
  const curX = new Float32Array(maxSeeds);
  const curY = new Float32Array(maxSeeds);
  // Uniform-grid buckets for the neighbour pass.
  const binOf = new Int32Array(maxSeeds);
  const nextInBin = new Int32Array(maxSeeds);
  let binHeads = new Int32Array(0);
  let binCols = 0;
  let binRows = 0;

  const pointer = { x: -10, y: -10, active: false };
  const curl = { x: 0, y: 0 };
  const split = { x: -1, y: -1, we: -1, active: false, life: 0 };

  let seeds = [];
  let aspect = 1;
  let gridN = 8;
  let borderW = 0.009;
  let cellSpacing = 0.1;
  let domX0 = 0;
  let domX1 = 1;
  let domY0 = 0;
  let domY1 = 1;
  let seedCount = 0;
  let uniformCount = 0;
  let splitCooldown = 4;
  let prevPointerX = -10;
  let prevPointerY = -10;
  let last = performance.now();

  function buildSeeds() {
    seeds = [];
    const rows = Math.max(1, Math.round(gridN));
    const cols = Math.max(1, Math.round(gridN * aspect));
    const cw = aspect / cols;
    const ch = 1 / rows;
    cellSpacing = Math.min(cw, ch);
    const margin = cellSpacing * 1.6;
    domX0 = -margin;
    domX1 = aspect + margin;
    domY0 = -margin;
    domY1 = 1 + margin;
    const extW = domX1 - domX0;
    const extH = domY1 - domY0;
    let ecols = Math.max(2, Math.round(extW / cw));
    let erows = Math.max(2, Math.round(extH / ch));
    const seedCap = Math.max(4, maxSeeds - 2);
    while (ecols * erows > seedCap && (ecols > 2 || erows > 2)) {
      if (ecols >= erows) ecols--;
      else erows--;
    }
    const ecw = extW / ecols;
    const ech = extH / erows;
    for (let j = 0; j < erows; j++) {
      for (let i = 0; i < ecols; i++) {
        seeds.push({
          bx: domX0 + (i + 0.5) * ecw + (Math.random() - 0.5) * ecw * 0.8,
          by: domY0 + (j + 0.5) * ech + (Math.random() - 0.5) * ech * 0.8,
          px: 0,
          py: 0,
          dx: 0,
          dy: 0,
          vx: 0,
          vy: 0,
          amp: (0.05 + Math.random() * 0.08) * Math.min(ecw, ech),
          w: 0,
          wphase: 0,
          wrate: 0,
          wamp: 0,
        });
      }
    }
    const cs2 = cellSpacing * cellSpacing;
    const wSpread = 1.5 * cs2;
    for (let i = 0; i < seeds.length && i < maxSeeds; i++) {
      seeds[i].w = (Math.random() - 0.5) * wSpread;
      seeds[i].wphase = Math.random() * 6.28;
      seeds[i].wrate = 0.12 + Math.random() * 0.35;
      seeds[i].wamp = (0.25 + Math.random() * 0.55) * cs2;
    }

    // Bucket grid sized to the interaction radius, so a seed only ever has to
    // look at the 3x3 block of buckets around it. Floor, not ceil: buckets have
    // to be at least `rest` wide or a pair that is still within range could sit
    // two buckets apart and never be tested.
    const bin = cellSpacing * 1.12;
    binCols = Math.max(1, Math.floor(extW / bin));
    binRows = Math.max(1, Math.floor(extH / bin));
    if (binHeads.length !== binCols * binRows) {
      binHeads = new Int32Array(binCols * binRows);
    }
  }

  // `force` is for the initial sizing, which has to build the seed field even on
  // the vanishingly rare chance the canvas already happens to be that size.
  function resize(force = false) {
    const w = host.clientWidth;
    const h = host.clientHeight;
    if (!w || !h) return false;

    let dpr = Math.sqrt(CONFIG.pixelBudget / (w * h));
    dpr = Math.max(CONFIG.minDpr, Math.min(CONFIG.maxDpr, dpr));
    const nextW = Math.max(2, Math.floor((w * dpr) / 2) * 2);
    const nextH = Math.max(2, Math.floor((h * dpr) / 2) * 2);

    // Nothing to do, and rebuilding anyway would reseed for free. Worth guarding
    // beyond the debounce: on mobile the address bar sliding away resizes the
    // block on every scroll, and often lands back on the same pixel size.
    if (!force && nextW === canvas.width && nextH === canvas.height) return false;

    canvas.width = nextW;
    canvas.height = nextH;
    gl.viewport(0, 0, canvas.width, canvas.height);

    aspect = w / h;
    gridN = Math.max(1, Math.round(h / opts.targetCellPx));
    // Line thickness is defined in screen px; uBorderW lives in p-space where
    // 1 unit = h px, so derive it from the current height and the channel keeps
    // the same px width at any size.
    borderW = opts.linePx / (2 * h);
    buildSeeds();
    return true;
  }

  function curlField(x, y, t) {
    const pot = (X, Y) =>
      Math.sin(X * 1.7 + t * 0.45) * Math.cos(Y * 1.3 - t * 0.4) +
      0.6 * Math.sin(X * 0.9 + Y * 1.1 - t * 0.3) +
      0.5 * Math.cos(Y * 2.1 + t * 0.27);
    const e = 0.012;
    curl.x = (pot(x, y + e) - pot(x, y - e)) / (2 * e);
    curl.y = -(pot(x + e, y) - pot(x - e, y)) / (2 * e);
  }

  // Seeds repel their close neighbours so cells stay roughly even. Only pairs
  // closer than `rest` contribute anything, so the uniform grid lets us skip
  // every distant pair instead of testing all of them.
  function coupleNeighbours(n, dt) {
    const rest = cellSpacing * 1.12;
    const coupleK = 8.0 * (0.6 + 0.4 * opts.ripple);
    const extW = domX1 - domX0;
    const extH = domY1 - domY0;
    const binW = extW / binCols;
    const binH = extH / binRows;

    binHeads.fill(-1);
    for (let i = 0; i < n; i++) {
      const cx = Math.min(
        binCols - 1,
        Math.max(0, Math.floor((curX[i] - domX0) / binW))
      );
      const cy = Math.min(
        binRows - 1,
        Math.max(0, Math.floor((curY[i] - domY0) / binH))
      );
      const b = cy * binCols + cx;
      binOf[i] = b;
      nextInBin[i] = binHeads[b];
      binHeads[b] = i;
    }

    for (let i = 0; i < n; i++) {
      const cx = binOf[i] % binCols;
      const cy = (binOf[i] - cx) / binCols;
      for (let oy = -1; oy <= 1; oy++) {
        const by = cy + oy;
        if (by < 0 || by >= binRows) continue;
        for (let ox = -1; ox <= 1; ox++) {
          const bx = cx + ox;
          if (bx < 0 || bx >= binCols) continue;
          for (let j = binHeads[by * binCols + bx]; j !== -1; j = nextInBin[j]) {
            // Each pair once.
            if (j <= i) continue;
            const rx = curX[i] - curX[j];
            const ry = curY[i] - curY[j];
            const dist = Math.hypot(rx, ry);
            if (dist < rest && dist > 1e-4) {
              const force = (1 - dist / rest) * coupleK * dt;
              const nx = (rx / dist) * force;
              const ny = (ry / dist) * force;
              seeds[i].vx += nx;
              seeds[i].vy += ny;
              seeds[j].vx -= nx;
              seeds[j].vy -= ny;
            }
          }
        }
      }
    }
  }

  function step(now) {
    let dt = (now - last) / 1000;
    last = now;
    if (dt > 0.05) dt = 0.05;
    const t = now / 1000;

    const reach = opts.reach;
    const pushK = opts.ripple * 0.32;
    const domW = domX1 - domX0;
    const domH = domY1 - domY0;
    const n = Math.min(seeds.length, maxSeeds);
    seedCount = n;

    const flowV = 0.04 * opts.speed;
    const fvx = flowV * aspect;
    const flowT = t * 0.5 * opts.speed;
    const mvx = pointer.active ? pointer.x - prevPointerX : 0;
    const mvy = pointer.active ? pointer.y - prevPointerY : 0;
    const mspeed = Math.hypot(mvx, mvy);
    const moving = mspeed > 1e-5;
    const mdx = moving ? mvx / mspeed : 0;
    const mdy = moving ? mvy / mspeed : 0;

    for (let i = 0; i < n; i++) {
      const s = seeds[i];
      s.by += flowV * dt;
      s.bx += fvx * dt;
      if (s.by > domY1) s.by -= domH;
      if (s.bx > domX1) s.bx -= domW;
      curlField(s.bx, s.by, flowT);
      s.px = s.bx + curl.x * s.amp * 1.4;
      s.py = s.by + curl.y * s.amp * 1.4;
      curX[i] = s.px + s.dx;
      curY[i] = s.py + s.dy;
      if (pointer.active) {
        const rx = curX[i] - pointer.x;
        const ry = curY[i] - pointer.y;
        const dist = Math.hypot(rx, ry);
        if (dist < reach && dist > 1e-4) {
          const f = 1 - dist / reach;
          const fall = f * f;
          const inv = 1 / dist;
          let fxp = rx * inv;
          let fyp = ry * inv;
          if (moving) {
            const along = rx * mdx + ry * mdy;
            const perpx = rx - along * mdx;
            const perpy = ry - along * mdy;
            const pl = Math.hypot(perpx, perpy) + 1e-5;
            const wake = Math.min(mspeed * 25, 1.1);
            fxp += (perpx / pl) * wake;
            fyp += (perpy / pl) * wake;
          }
          s.vx += fxp * pushK * fall * dt * 60;
          s.vy += fyp * pushK * fall * dt * 60;
        }
      }
    }

    coupleNeighbours(n, dt);

    const spring = 12.0;
    const damp = 3.4;
    for (let i = 0; i < n; i++) {
      const s = seeds[i];
      s.vx += (-spring * s.dx - damp * s.vx) * dt;
      s.vy += (-spring * s.dy - damp * s.vy) * dt;
      s.dx += s.vx * dt;
      s.dy += s.vy * dt;
      seedData[i * 3] = s.px + s.dx;
      seedData[i * 3 + 1] = s.py + s.dy;
      seedData[i * 3 + 2] = s.w + Math.sin(t * s.wrate + s.wphase) * s.wamp;
    }

    // Occasionally a cell splits in two under the pointer, then heals.
    const splitW = cellSpacing * cellSpacing;
    const dur = 1.5;
    prevPointerX = pointer.x;
    prevPointerY = pointer.y;
    splitCooldown -= dt;
    if (
      pointer.active &&
      moving &&
      !split.active &&
      splitCooldown <= 0 &&
      Math.random() < 0.8 * dt
    ) {
      split.active = true;
      split.life = 0;
      split.x = pointer.x;
      split.y = pointer.y;
      splitCooldown = 7 + Math.random() * 8;
    }
    if (split.active) {
      split.life += dt;
      const u = split.life / dur;
      if (u >= 1) {
        split.active = false;
        split.we = -3 * splitW;
      } else {
        split.we = splitW * Math.sin(u * Math.PI) - 0.03;
      }
    } else {
      split.we = -3 * splitW;
    }

    uniformCount = n;
    if (uniformCount < maxSeeds) {
      seedData[uniformCount * 3] = split.x;
      seedData[uniformCount * 3 + 1] = split.y;
      seedData[uniformCount * 3 + 2] = split.we;
      uniformCount++;
    }
  }

  function draw() {
    // Several contexts share the page; never assume ours is still current.
    gl.useProgram(program);
    gl.bindBuffer(gl.ARRAY_BUFFER, quad);
    gl.uniform3fv(uniforms.seedW, seedData);
    gl.uniform1i(uniforms.count, uniformCount || seedCount);
    gl.uniform3fv(uniforms.cell, waterColor);
    gl.uniform3fv(uniforms.border, lineColor);
    gl.uniform3fv(uniforms.border2, lineColor2);
    gl.uniform1f(uniforms.aspect, aspect);
    gl.uniform1f(uniforms.borderW, borderW);
    gl.uniform1f(uniforms.round, borderW * (1.0 + opts.roundness * 4.0));
    gl.uniform1f(uniforms.linesOnly, opts.linesOnly ? 1 : 0);
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
  }

  function setPointer(clientX, clientY) {
    const rect = canvas.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    const x = (clientX - rect.left) / rect.width;
    const y = (clientY - rect.top) / rect.height;
    // Outside this block the water settles back rather than chasing a pointer
    // that belongs to someone else's canvas.
    if (x < 0 || x > 1 || y < 0 || y > 1) {
      pointer.active = false;
      return;
    }
    pointer.x = x * aspect;
    pointer.y = y;
    pointer.active = true;
  }

  function clearPointerState() {
    pointer.active = false;
  }

  function resetClock() {
    last = performance.now();
  }

  function destroy() {
    gl.getExtension("WEBGL_lose_context")?.loseContext();
  }

  if (!resize(true)) {
    destroy();
    return null;
  }

  return {
    step,
    draw,
    resize,
    setPointer,
    clearPointer: clearPointerState,
    resetClock,
    destroy,
  };
}

// ------------------------------------------------------------------ controller

// One per block. Owns when its simulation exists and when it runs; the
// simulation itself owns what it looks like.
function createController(host, canvas) {
  let sim = null;
  let visible = false;
  let unavailable = false;
  let lastVisibleAt = 0;
  let resizeTimer = 0;

  function showFallback(on) {
    if (on) host.dataset.waterFallback = "";
    else delete host.dataset.waterFallback;
  }

  function ensureSim() {
    if (sim || unavailable) return sim;
    // No box yet (a hidden parent, say). Not a failure — try again next time.
    if (!host.clientWidth || !host.clientHeight) return null;
    if (!makeRoomForContext()) return null;

    sim = createSimulation(host, canvas, readOptions(host));
    if (!sim) {
      // No WebGL at all, or the shader would not link. Flat colour from here on;
      // retrying on every scroll would just burn frames.
      unavailable = true;
      showFallback(true);
      return null;
    }
    showFallback(false);
    live.add(controller);
    return sim;
  }

  // Called by the pool when another block needs the context more than we do.
  function releaseContext() {
    if (!sim) return;
    clearTimeout(resizeTimer);
    resizeTimer = 0;
    playing.delete(controller);
    live.delete(controller);
    sim.destroy();
    sim = null;
    showFallback(true);
  }

  function play() {
    if (!ensureSim()) return;
    if (reduceMotion.matches) {
      // A single frame, then nothing. No loop, no ticker slot.
      playing.delete(controller);
      sim.resetClock();
      sim.step(performance.now() + 16);
      sim.draw();
      return;
    }
    if (playing.has(controller)) return;
    sim.resetClock();
    playing.add(controller);
    startTicker();
  }

  function stop() {
    playing.delete(controller);
  }

  const controller = {
    get isVisible() {
      return visible;
    },
    get lastVisibleAt() {
      return lastVisibleAt;
    },
    advance(now) {
      sim.step(now);
      sim.draw();
    },
    trackPointer(x, y) {
      sim?.setPointer(x, y);
    },
    clearPointer() {
      sim?.clearPointer();
    },
    releaseContext,
    enter() {
      visible = true;
      play();
    },
    leave() {
      visible = false;
      lastVisibleAt = performance.now();
      stop();
    },
    handleResize() {
      if (!sim) return;
      // Coalesced: only the last resize in a burst rebuilds anything.
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        resizeTimer = 0;
        if (!sim || !sim.resize()) return;
        // Repaint a block that is on screen but not looping (reduced motion),
        // otherwise it would keep showing a frame at the old size.
        if (visible && !playing.has(controller)) play();
      }, CONFIG.resizeDebounceMs);
    },
    handleMotionChange() {
      if (!visible) return;
      stop();
      play();
    },
  };

  return controller;
}

// ------------------------------------------------------------------------ init

function init() {
  const hosts = document.querySelectorAll("[data-water-animation]");
  if (!hosts.length) return;

  bindPointer();

  const controllers = new Map();

  const observer = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        const controller = controllers.get(entry.target);
        if (!controller) return;
        if (entry.isIntersecting) controller.enter();
        else controller.leave();
      });
    },
    { rootMargin: CONFIG.rootMargin }
  );

  const resizeObserver = new ResizeObserver((entries) => {
    entries.forEach((entry) => controllers.get(entry.target)?.handleResize());
  });

  hosts.forEach((host) => {
    const canvas = host.querySelector("[data-water-animation-canvas]");
    if (!canvas) return;
    controllers.set(host, createController(host, canvas));
    observer.observe(host);
    resizeObserver.observe(host);
  });

  reduceMotion.addEventListener("change", () => {
    controllers.forEach((controller) => controller.handleMotionChange());
  });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init);
} else {
  init();
}
